/**
 * Formateadores y utilidades puras.
 *
 * Los formateadores se crean **por demanda**, no al importar el módulo: la
 * configuración (moneda, locale) llega de la API y, si se armaran al
 * importar, quedarían con los valores del seed.
 */

import type { ConfigResuelta } from './tipos';

/** Configuración activa. La fija `cargarConfig()` al renderizar. */
let pricing = {
  currency: 'ARS',
  locale: 'es-AR',
  includesTax: true,
  taxLabel: 'IVA incluido',
};

/** Fija la configuración de precios (la resuelve `cargarConfig()`). */
export function setPricing(config: Pick<ConfigResuelta, 'pricing'>): void {
  pricing = config.pricing;
}

/**
 * El formateador de la moneda de la tienda.
 *
 * Una moneda inválida en la API no puede voltear el build entero: se
 * formatea sin símbolo en vez de romper el sitio.
 */
function formateador(decimales: boolean): Intl.NumberFormat {
  const opciones: Intl.NumberFormatOptions = {
    minimumFractionDigits: decimales ? 2 : 0,
    maximumFractionDigits: decimales ? 2 : 0,
  };
  try {
    return new Intl.NumberFormat(pricing.locale, {
      ...opciones,
      style: 'currency',
      currency: pricing.currency,
    });
  } catch {
    return new Intl.NumberFormat(pricing.locale, { ...opciones, style: 'decimal' });
  }
}

/** Precio en la moneda de la tienda. */
export function precio(valor: number, { decimales = false } = {}): string {
  return formateador(decimales).format(valor);
}

/** Precio sin símbolo, para `aria-label` y atributos `data-*`. */
export function precioNumero(valor: number): string {
  return new Intl.NumberFormat(pricing.locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(valor);
}

/** Normaliza un texto a slug (para URLs). */
export function slugify(texto: string): string {
  return String(texto ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Une clases ignorando los valores falsy. */
export function cx(...partes: (string | false | null | undefined)[]): string {
  return partes.filter(Boolean).join(' ');
}

/**
 * Normaliza un nombre de ciudad para comparar.
 *
 * "Eldorado" y "eldorado" son la misma zona; "Posadas " y "Posadas" también.
 * Sin esto, el envío gratis no se reconoce por mayúsculas ni por espacios.
 */
export function normalizarCiudad(nombre: string): string {
  return String(nombre ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}
